import {
  AFFINITY_TYPES,
  AWAKEN,
  FORGE,
  DAY_BASE_BP,
  GEAR_ATTRS,
  GEAR_BASE_IDS,
  LOOT_FAVOR,
  LOOT_FAVOR_BP,
  OTHER_CLASS_BP,
  type LootPlace,
  GEAR_SETS,
  SET_DROP_BP,
  SET_IDS,
  TITAN_MARK_LEVEL,
  type SetId,
  GEAR_BASES,
  GEAR_EFFECTS,
  GEAR_ROLL_BP,
  GEAR_SLOTS,
  POWER,
  RARITY_ATTRS,
  RARITY_WEIGHTS,
  REFORGE,
  SLOT_DROP_WEIGHT,
  SLOT_EFFECTS,
  SLOT_LEVEL,
  SOCKETS_BY_SLOT,
  SOCKETS_TWO_HANDED,
  STRETCHED_ATTRS,
  SLOT_BUDGET_BP,
  UNIQUE_ATTRS,
  UNIQUE_STRETCH,
  UNIQUES,
  PANOPLY,
  attrOnSlot,
  baseAttrs,
  baseBudget,
  basesFor,
  canWearBase,
  isResAttr,
  legacyBase,
  powerBp,
  uniqueFor,
  WORN_SLOTS,
  type GearAttr,
  type GearBaseId,
  type GearEffect,
  type GearSlot,
  type GemCut,
  type GemId,
  type HeroClass,
  type SealId,
  type UniqueSlot,
  type Weight,
  type WornSlot,
} from '@somnhold/content';
import { imax, mulBp } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngRange, rngWeighted, seedRng, type RngState } from '../math/rng.ts';
import { event } from './core.ts';
import { ensureSlot, slotFeat, syncSlot, uniquePieces } from './panoply.ts';
import type { ArmoryFind, CampaignState, HeroState, ItemInstance, Ms, PlayerState, Rarity } from './types.ts';

/** The attributes a piece could still gain: its base's (an old piece: its slot's), affinity only if it has none yet (one type per piece). */
const openAttrs = (attrs: Partial<Record<GearAttr, number>>, item: Pick<ItemInstance, 'slot' | 'base'>): GearAttr[] => {
  const hasRes = (Object.keys(attrs) as GearAttr[]).some(isResAttr);
  const allowed = item.base ? baseAttrs(item.base) : null;
  return GEAR_ATTRS.filter((k) => attrs[k] === undefined && (allowed ? allowed.includes(k) : attrOnSlot(k, item.slot)) && !(hasRes && isResAttr(k)));
};

/** Draws one of `pool`, the affinities counting as a single choice (then its type drawn among them). */
const drawAttr = (rng: RngState, pool: GearAttr[]): GearAttr => {
  const plain = pool.filter((k) => !isResAttr(k));
  const res = pool.filter(isResAttr);
  const i = rngInt(rng, plain.length + (res.length ? 1 : 0));
  return i < plain.length ? (plain[i] as GearAttr) : (res[rngInt(rng, res.length)] as GearAttr);
};

/** The base a piece is: its own, or (a piece from before bases) the usual one of `cls` for its slot. */
export const itemBase = (item: ItemInstance, cls: HeroClass): GearBaseId => item.base ?? legacyBase(cls, item.slot);

/**
 * An attribute's roll range on a piece (docs/EQUIPO.md §1, §4): its rarity's, times its base's budget and,
 * for the attributes without a cap, its power. A piece from before bases rolls in the rarity's range.
 */
export const rollRange = (item: Pick<ItemInstance, 'base' | 'power'>, rarity: Rarity, k: GearAttr): [number, number] => {
  const [lo, hi] = GEAR_ROLL_BP[rarity];
  if (!item.base) return [lo, hi];
  let bp = baseBudget(item.base);
  if (item.power !== undefined && STRETCHED_ATTRS.includes(k)) bp = mulBp(bp, powerBp(item.power));
  return [mulBp(lo, bp), mulBp(hi, bp)];
};

/** The rare effects a piece can carry: its slot's (a piece from before bases: any). */
export const effectPool = (item: Pick<ItemInstance, 'base' | 'slot'>): GearEffect[] => (item.base ? SLOT_EFFECTS[item.slot] : GEAR_EFFECTS);

/** What a roll knows of who gets it: the class and level of the hero, and the piece's power (level + place). */
export interface RollCtx {
  cls: HeroClass;
  level: number;
  power: number;
  /** A slot already drawn (a warlord's piece goes to whoever needs that slot most). */
  slot?: GearSlot;
  /** The classes of the group: OTHER_CLASS_BP of the pieces with a class are for one of them (to give away). */
  group?: HeroClass[];
  /** The slot the place drops more of (LOOT_FAVOR). */
  favor?: GearSlot;
  /** The day's favoured base (dayLootBase). */
  dayBase?: GearBaseId;
  /** The set this place drops pieces of, the hero having its level (GEAR_SETS). */
  set?: SetId;
}

/** The slots open to a hero at a level, weighted for a drop (SLOT_DROP_WEIGHT). */
export const rollSlot = (rng: RngState, level: number, favor?: GearSlot): GearSlot =>
  rngWeighted(
    rng,
    (Object.keys(SLOT_DROP_WEIGHT) as GearSlot[]).filter((k) => level >= SLOT_LEVEL[k]).map((k) => [k, k === favor ? mulBp(SLOT_DROP_WEIGHT[k], LOOT_FAVOR_BP) : SLOT_DROP_WEIGHT[k]] as const),
  );

/** The base a campaign day favours (docs/EQUIPO.md §6): the same for everyone all day, shown with the omen. */
export const dayLootBase = (s: CampaignState, day: number): GearBaseId => {
  const rng = seedRng(deriveSeed(s.seed, 7400 + day));
  return GEAR_BASE_IDS[rngInt(rng, GEAR_BASE_IDS.length)] as GearBaseId;
};

/**
 * Rolls a piece of gear (SPEC §14, docs/EQUIPO.md). Without `ctx`, as before bases: one of the three old
 * slots, no base, no power. With it: a slot open to the hero, a base its class wears, a rarity (very rare
 * from POWER.veryRareFrom), attributes in ranges stretched by the base's budget and the power, an effect
 * from its slot's pool and, for a talisman or a ward ring, the damage type it guards against.
 */
export const rollItem = (s: CampaignState, rng: RngState, rarity?: Rarity, ctx?: RollCtx): ItemInstance => {
  if (!ctx) {
    const r: Rarity = rarity ?? rngWeighted(rng, [['common', RARITY_WEIGHTS.common], ['uncommon', RARITY_WEIGHTS.uncommon], ['rare', RARITY_WEIGHTS.rare]] as const);
    const slot = GEAR_SLOTS[rngInt(rng, GEAR_SLOTS.length)] as UniqueSlot;
    const attrs: Partial<Record<GearAttr, number>> = {};
    const [lo, hi] = GEAR_ROLL_BP[r];
    for (let i = 0; i < RARITY_ATTRS[r]; i++) {
      const k = drawAttr(rng, openAttrs(attrs, { slot }));
      attrs[k] = rngRange(rng, lo, hi);
    }
    return { id: s.nextId++, slot, rarity: r, attrs, effect: r === 'rare' ? (GEAR_EFFECTS[rngInt(rng, GEAR_EFFECTS.length)] as ItemInstance['effect']) : null, plus: 0 };
  }
  // A set's piece now and then, at its place (docs/EQUIPO.md §7): one of its slots, rare at least.
  // (A slot already drawn, as a warlord's, can be a set's piece too when it is one of its slots.)
  const setDef = ctx.set && (!ctx.slot || GEAR_SETS[ctx.set].slots.includes(ctx.slot)) && rngChance(rng, SET_DROP_BP) ? GEAR_SETS[ctx.set] : null;
  const slot = ctx.slot ?? (setDef ? (setDef.slots[rngInt(rng, setDef.slots.length)] as GearSlot) : rollSlot(rng, ctx.level, ctx.favor));
  // Some of what falls is for another class of the group, to give away (neck and fingers have no class).
  const classed = slot !== 'amulet' && slot !== 'ring';
  const cls = classed && ctx.group?.length && rngChance(rng, OTHER_CLASS_BP) ? (ctx.group[rngInt(rng, ctx.group.length)] as HeroClass) : ctx.cls;
  const bases = basesFor(cls, slot);
  const base = rngWeighted(rng, bases.map((b) => [b, b === ctx.dayBase ? DAY_BASE_BP : 10000] as const));
  const deep = ctx.power >= POWER.veryRareFrom;
  const rolled: Rarity =
    rarity ??
    rngWeighted(rng, [
      ['common', RARITY_WEIGHTS.common],
      ['uncommon', RARITY_WEIGHTS.uncommon],
      ['rare', RARITY_WEIGHTS.rare - (deep ? POWER.veryRareBp : 0)],
      ['veryRare', deep ? POWER.veryRareBp : 0],
    ] as const);
  const r: Rarity = setDef && (rolled === 'common' || rolled === 'uncommon') ? 'rare' : rolled;
  const item: ItemInstance = { id: s.nextId++, slot, base, power: ctx.power, rarity: r, attrs: {}, effect: null, plus: 0, ...(setDef && ctx.set ? { set: ctx.set } : {}) };
  for (let i = 0; i < RARITY_ATTRS[r]; i++) {
    const pool = openAttrs(item.attrs, item);
    if (pool.length === 0) break;
    const k = drawAttr(rng, pool);
    const [lo, hi] = rollRange(item, r, k);
    item.attrs[k] = rngRange(rng, lo, hi);
  }
  if (r === 'rare' || r === 'veryRare') {
    const fx = effectPool(item);
    item.effect = fx[rngInt(rng, fx.length)] as GearEffect;
  }
  if (GEAR_BASES[base].trait.resBp) item.res = AFFINITY_TYPES[rngInt(rng, AFFINITY_TYPES.length)];
  return item;
};

/**
 * A forge recipe's piece (docs/SABERES.md §4.4): uncommon, at the hero's power, of the recipe's base, bound to the
 * hero. Its attributes roll like any uncommon of that base and power.
 */
export const recipePiece = (s: CampaignState, p: PlayerState, base: GearBaseId): ItemInstance => {
  const item: ItemInstance = { id: s.nextId++, slot: GEAR_BASES[base].slot, base, power: p.hero.level, rarity: 'uncommon', attrs: {}, effect: null, plus: 0, bound: true };
  for (let i = 0; i < RARITY_ATTRS.uncommon; i++) {
    const pool = openAttrs(item.attrs, item);
    if (pool.length === 0) break;
    const k = drawAttr(s.rng, pool);
    const [lo, hi] = rollRange(item, 'uncommon', k);
    item.attrs[k] = rngRange(s.rng, lo, hi);
  }
  return item;
};

/**
 * One rarity step up (tempering): every attribute it has rises by the step between the two rarities'
 * floors (and to at least the new floor, at most its ceiling), a rare piece gains its effect, and an
 * uncommon one a second attribute. Its pluses stay.
 */
export const temperItem = (s: CampaignState, item: ItemInstance, to: 'uncommon' | 'rare'): void => {
  for (const k of Object.keys(item.attrs).sort() as GearAttr[]) {
    const [lo, hi] = rollRange(item, to, k);
    const step = lo - rollRange(item, item.rarity, k)[0];
    const v = (item.attrs[k] ?? 0) + step;
    item.attrs[k] = v < lo ? lo : v > hi ? hi : v;
  }
  while (Object.keys(item.attrs).length < RARITY_ATTRS[to] && openAttrs(item.attrs, item).length) {
    const k = drawAttr(s.rng, openAttrs(item.attrs, item));
    const [lo, hi] = rollRange(item, to, k);
    item.attrs[k] = rngRange(s.rng, lo, hi);
  }
  if (to === 'rare' && !item.effect) {
    const fx = effectPool(item);
    item.effect = fx[rngInt(s.rng, fx.length)] as GearEffect;
  }
  item.rarity = to;
};

export const MAX_BAG = 20;

/**
 * What counts against MAX_BAG: the bag, and (until the panoply's second stage, docs/PANOPLIA.md §11) the unique pieces
 * not worn, as when they were kept in the bag, so that what a full bag drops doesn't change.
 */
export const bagCount = (h: HeroState): number => h.bag.length + (PANOPLY.inBag ? (h.panoply?.length ?? 0) : 0);

/** Why a hero can't wear a piece (in the slot it would go): each one has its refusal text (reason.*). */
export const WEAR_BLOCKS = ['notYourClass', 'slotLocked', 'tooPowerful', 'twoHanded'] as const;
export type WearBlock = (typeof WEAR_BLOCKS)[number];

/** The worn slot a piece goes to: its own; a ring to the first ring slot free (`ring`: 0 or 1 to say which). */
export const wornSlotFor = (h: HeroState, item: ItemInstance, ring?: 0 | 1): WornSlot => {
  if (item.slot !== 'ring') return item.slot;
  if (ring !== undefined) return ring === 0 ? 'ring' : 'ring2';
  return h.gear.ring && h.level >= SLOT_LEVEL.ring2 && !h.gear.ring2 ? 'ring2' : 'ring';
};

/** Whether a two-handed weapon is worn: a unique or older piece by its class's usual weapon, as the sheet shows it. */
export const twoHanded = (h: HeroState): boolean => {
  const w = h.gear.weapon;
  return !!w && GEAR_BASES[itemBase(w, h.heroClass)].hands === 2;
};

/** Why `h` can't wear `item` at `at`: its class and weight, the slot's level, the piece's power, a two-handed weapon. */
export const wearBlock = (h: HeroState, item: ItemInstance, at: WornSlot = wornSlotFor(h, item)): WearBlock | null => {
  if (item.base && !canWearBase(h.heroClass, item.base)) return 'notYourClass';
  if (h.level < SLOT_LEVEL[at]) return 'slotLocked';
  if (item.power !== undefined && item.power - POWER.slack > h.level) return 'tooPowerful';
  if (at === 'offhand' && twoHanded(h)) return 'twoHanded';
  return null;
};

/** The pieces the hero's preparations go in with (by item id). */
export const preparedIds = (h: HeroState): Set<number> => {
  const out = new Set<number>();
  for (const l of h.loadouts ?? []) for (const id of Object.values(l.gear ?? {})) if (id !== undefined) out.add(id);
  return out;
};

/**
 * What the hero wears going in with a preparation's pieces (`want`, by item id per slot): each from the
 * worn ones or the bag, once, if it can be worn there; a slot left out, or whose piece is gone (given,
 * melted) or can't be worn, takes what is worn there unless that went elsewhere. A two-handed weapon
 * leaves the off-hand empty. Without `want`, what is worn.
 */
export const preparedGear = (h: HeroState, want: Partial<Record<WornSlot, number>> | undefined): HeroState['gear'] => {
  if (!want) return h.gear;
  const owned = new Map<number, ItemInstance>();
  for (const slot of WORN_SLOTS) {
    const it = h.gear[slot];
    if (it) owned.set(it.id, it);
  }
  for (const it of h.bag) owned.set(it.id, it);
  for (const it of h.panoply ?? []) owned.set(it.id, it);
  const out: HeroState['gear'] = {};
  const used = new Set<number>();
  const probe: HeroState = { ...h, gear: out };
  // A piece asked for in one slot never fills another as what is worn there (a ring moved to the other hand).
  const asked = new Set<number>();
  for (const slot of WORN_SLOTS) if (want[slot] !== undefined && owned.has(want[slot])) asked.add(want[slot]);
  const fits = (it: ItemInstance | undefined, slot: WornSlot): it is ItemInstance =>
    !!it && !used.has(it.id) && (it.slot === slot || (it.slot === 'ring' && slot === 'ring2')) && !wearBlock(probe, it, slot);
  // The weapon first: whether it takes both hands decides the off-hand.
  for (const slot of WORN_SLOTS) {
    const id = want[slot];
    const worn = h.gear[slot];
    const pick = id !== undefined && fits(owned.get(id), slot) ? owned.get(id) : worn && !asked.has(worn.id) && fits(worn, slot) ? worn : undefined;
    if (!pick) continue;
    out[slot] = pick;
    used.add(pick.id);
  }
  return out;
};

/** Gives an item: straight into its empty slot if the hero can wear it, else into the bag (oldest common drops out when full). */
export const giveItem = (s: CampaignState, p: PlayerState, item: ItemInstance, now: Ms, unique?: string): void => {
  const g = p.hero.gear;
  const at = wornSlotFor(p.hero, item);
  if (!g[at] && !wearBlock(p.hero, item, at)) {
    g[at] = item;
    if (item.unique) ensureSlot(p.hero, item.slot as UniqueSlot).last = item.id;
  } else {
    // A unique piece waits in the panoply, out of the bag (docs/PANOPLIA.md §5).
    if (item.unique) (p.hero.panoply ??= []).push(item);
    else p.hero.bag.push(item);
    if (bagCount(p.hero) > MAX_BAG) {
      // The oldest common goes, else the oldest that isn't bound to the hero (a unique piece never drops out).
      // A piece a preparation goes in with is kept too.
      const kept = preparedIds(p.hero);
      let i = p.hero.bag.findIndex((x) => x.rarity === 'common' && !kept.has(x.id));
      if (i < 0) i = p.hero.bag.findIndex((x) => !x.unique && !kept.has(x.id));
      if (i >= 0) p.hero.bag.splice(i, 1);
    }
  }
  event(s, now, 'loot', { pid: p.id, data: { rarity: item.rarity, slot: item.slot, item: item.id, ...(unique ? { unique } : {}) } });
  noteFind(s, item, p.id, now);
};

/**
 * Equips a bag item, swapping with what was in that slot. A two-handed weapon sends the off-hand to the
 * bag. Returns why it couldn't (or null).
 */
export const equipItem = (p: PlayerState, itemId: number, ring?: 0 | 1): WearBlock | 'noItem' | null => {
  const h = p.hero;
  // From the bag, or a unique piece from the panoply (docs/PANOPLIA.md §5).
  const i = h.bag.findIndex((x) => x.id === itemId);
  const j = i < 0 ? (h.panoply ?? []).findIndex((x) => x.id === itemId) : -1;
  if (i < 0 && j < 0) return 'noItem';
  const item = (i >= 0 ? h.bag[i] : h.panoply?.[j]) as ItemInstance;
  const at = wornSlotFor(h, item, ring);
  const block = wearBlock(h, item, at);
  if (block) return block;
  const old = h.gear[at];
  if (i >= 0) h.bag.splice(i, 1);
  else h.panoply?.splice(j, 1);
  h.gear[at] = item;
  if (item.unique) ensureSlot(h, item.slot as UniqueSlot).last = item.id;
  // A unique piece taken off goes back to the panoply, never to the bag.
  if (old?.unique) (h.panoply ??= []).push(old);
  else if (old) h.bag.push(old);
  if (at === 'weapon' && twoHanded(h) && h.gear.offhand) {
    h.bag.push(h.gear.offhand);
    delete h.gear.offhand;
  }
  return null;
};

/**
 * The i18n keys of a piece's name (docs/EQUIPO.md §9): its base and a suffix (none for a common piece; the
 * main attribute for an uncommon one; the effect for a rare or very rare one). A unique piece has its own name.
 */
export const itemNameKeys = (item: ItemInstance, cls: HeroClass): { base: string; suffix: string | null } => {
  if (item.unique) return { base: `unique.${item.unique}`, suffix: null };
  const base = `gear.base.${itemBase(item, cls)}`;
  if (item.set) return { base, suffix: `gear.set.${item.set}` };
  if (item.effect && (item.rarity === 'rare' || item.rarity === 'veryRare')) return { base, suffix: `gear.suffix.${item.effect}` };
  const main = item.rarity === 'common' ? undefined : mainAttr(item);
  return { base, suffix: main ? `gear.suffix.${main}` : null };
};

/** The set a place drops pieces of for a hero at this level (none below its level). */
const setAt = (place: LootPlace | undefined, level: number): SetId | undefined =>
  place ? SET_IDS.find((id) => GEAR_SETS[id].place === place && level >= GEAR_SETS[id].level) : undefined;

/**
 * What a drop for `p` knows (docs/EQUIPO.md §4, §6): its class and level, the power its place adds, the group's
 * classes, the slot the place favours, the day's base when the day is known, and `slot` when already drawn.
 */
export const lootCtx = (s: CampaignState, p: PlayerState, bonus: number, place?: LootPlace, day?: number, slot?: GearSlot): RollCtx => {
  const group = [...new Set(s.players.filter((x) => !x.left).map((x) => x.hero.heroClass))].sort();
  return {
    cls: p.hero.heroClass,
    level: p.hero.level,
    power: p.hero.level + bonus,
    group,
    ...(place ? { favor: LOOT_FAVOR[place] } : {}),
    ...(day !== undefined ? { dayBase: dayLootBase(s, day) } : {}),
    ...(slot ? { slot } : {}),
    ...(setAt(place, p.hero.level) ? { set: setAt(place, p.hero.level) as SetId } : {}),
  };
};

/** What a piece's slot is worth (bp): its base's budget (docs/EQUIPO.md §1); a piece from before bases, a whole slot. */
export const itemBudget = (item: ItemInstance): number => (item.base ? baseBudget(item.base) : 10000);

/** Whether a hero wears head, chest and feet all of one weight (their traits count more); the weight, or null. */
export const weightSetOf = (h: HeroState): Weight | null => {
  const w = (k: 'head' | 'armor' | 'boots'): Weight | undefined => {
    const it = h.gear[k];
    return it ? GEAR_BASES[itemBase(it, h.heroClass)].weight : undefined;
  };
  const a = w('head');
  return a && a === w('armor') && a === w('boots') ? a : null;
};

/**
 * What shows of a hero's gear on its miniature (docs/EQUIPO.md §10), as a short string for the model's key:
 * main hand ~ its rarity ~ off hand ~ its rarity ~ head ~ chest ~ feet, `-` for nothing worn there and `u` for a unique
 * piece (drawn by the class's own model). Undefined with no piece with a base worn (the class's model as it was).
 * A ninth field, last, only once the hero earned pieces of its path's look (docs/SENDAS.md): `S` and how many (the
 * titan's mark before it, empty without it), so every look from before reads and keys as it always did.
 */
export const heroLook = (h: HeroState): string | undefined => {
  const g = h.gear;
  // A piece from before bases shows as the class always drew it (nobody changes the day bases ship, §11);
  // with nothing newer worn, the class's own model.
  const shown = (it: ItemInstance | undefined): ItemInstance | undefined => (it?.base && !it.unique ? it : undefined);
  const titan = h.level >= TITAN_MARK_LEVEL;
  const path = h.senda?.pieces?.length ?? 0;
  if (!titan && !path && ![g.weapon, g.offhand, g.head, g.armor, g.boots].some(shown)) return undefined;
  const part = (it: ItemInstance | undefined): string => (it?.unique ? 'u' : shown(it) ? itemBase(it as ItemInstance, h.heroClass) : '-');
  const rar = (it: ItemInstance | undefined): string => (shown(it) ? (it as ItemInstance).rarity : '-');
  // The titan's mark (level 50) closes the look: the miniature wears its own glow.
  return [part(g.weapon), rar(g.weapon), part(g.offhand), rar(g.offhand), part(g.head), part(g.armor), part(g.boots), ...(titan || path ? [titan ? 'T' : ''] : []), ...(path ? [`S${path}`] : [])].join('~');
};
/** The look as a hero init's field (absent with nothing worn). */
export const lookOf = (h: HeroState): { look?: string } => {
  const look = heroLook(h);
  return look ? { look } : {};
};

/** A piece's settings: its own, or the one obsidian setting pieces had before (a whole gem). */
export const itemSockets = (item: ItemInstance): ({ gem: GemId; cut: GemCut } | null)[] => {
  if (item.sockets) return item.sockets;
  if (item.socket) return [item.socket.gem ? { gem: item.socket.gem, cut: 1 } : null];
  return [];
};
/** The settings a piece can have (docs/EQUIPO.md §5): its slot's (a two-handed weapon's more), one less on a rare piece, one on common and uncommon. */
export const socketMax = (item: ItemInstance): number => {
  const slotMax = item.base && GEAR_BASES[item.base].hands === 2 ? SOCKETS_TWO_HANDED : SOCKETS_BY_SLOT[item.slot];
  if (item.rarity === 'veryRare' || item.rarity === 'unique') return slotMax;
  if (item.rarity === 'rare') return imax(1, slotMax - 1);
  return 1;
};
/** A gem kept in the hero's pouch: its key. */
export const gemKey = (gem: GemId, cut: GemCut): string => `${gem}:${cut}`;
/** Puts gems in (n > 0) or takes them out of the hero's pouch. */
export const pouch = (h: HeroState, gem: GemId, cut: GemCut, n: number): void => {
  const k = gemKey(gem, cut);
  const v = (h.gems?.[k] ?? 0) + n;
  const gems = { ...h.gems };
  if (v > 0) gems[k] = v;
  else delete gems[k];
  if (Object.keys(gems).length) h.gems = gems;
  else delete h.gems;
};

/** A find that beats another for the armory: rarer, then more powerful. */
const betterFind = (a: { rarity: Rarity; power: number }, b: { rarity: Rarity; power: number } | undefined): boolean =>
  !b || RARITY_RANK[a.rarity] > RARITY_RANK[b.rarity] || (RARITY_RANK[a.rarity] === RARITY_RANK[b.rarity] && a.power > b.power);

/** A piece with a base found by `pid`: the armory keeps it if it is that base's best yet (unique pieces are the saga's, not the armory's). */
export const noteFind = (s: CampaignState, item: ItemInstance, pid: string, now: Ms): void => {
  if (!item.base || item.unique) return;
  const f = { rarity: item.rarity, power: item.power ?? 0, pid, at: now };
  if (betterFind(f, s.armory?.[item.base])) s.armory = { ...s.armory, [item.base]: f };
};

/**
 * What the armory shows (docs/EQUIPO.md §6): its noted finds, and any better piece someone holds now (pieces
 * found before the armory kept count, or gifted, count for whoever holds them).
 */
export const armoryShowcase = (s: CampaignState): Partial<Record<GearBaseId, ArmoryFind>> => {
  const out: Partial<Record<GearBaseId, ArmoryFind>> = { ...s.armory };
  for (const p of s.players) {
    for (const it of [...wornItems(p.hero), ...p.hero.bag]) {
      if (!it.base || it.unique) continue;
      const f = { rarity: it.rarity, power: it.power ?? 0, pid: p.id, at: 0 };
      if (betterFind(f, out[it.base])) out[it.base] = f;
    }
  }
  return out;
};

/** The armory's rack in the bastion: its best `n` finds, rarest and most powerful first. */
export const armoryRack = (s: CampaignState, n: number): { base: GearBaseId; rarity: Rarity }[] => {
  const all = armoryShowcase(s);
  return (Object.keys(all).sort() as GearBaseId[])
    .map((base) => ({ base, f: all[base] as ArmoryFind }))
    .sort((a, b) => RARITY_RANK[b.f.rarity] - RARITY_RANK[a.f.rarity] || b.f.power - a.f.power || (a.base < b.base ? -1 : 1))
    .slice(0, n)
    .map((x) => ({ base: x.base, rarity: x.f.rarity }));
};

/** Every piece a hero wears, in WORN_SLOTS order. */
export const wornItems = (h: HeroState): ItemInstance[] => WORN_SLOTS.map((w) => h.gear[w]).filter((x): x is ItemInstance => !!x);

export const findItem = (p: PlayerState, itemId: number): ItemInstance | undefined => [...wornItems(p.hero), ...p.hero.bag, ...(p.hero.panoply ?? [])].find((x) => x.id === itemId);

/** The main attribute (the first one, by name): the one the forge raises. */
export const mainAttr = (item: ItemInstance): GearAttr | undefined => (Object.keys(item.attrs).sort() as GearAttr[])[0];

/** Where the item's pluses went: as forged, or (older items) all on the main attribute. */
export const forgedOf = (item: ItemInstance): Partial<Record<GearAttr, number>> => {
  if (item.forged) return item.forged;
  const k = mainAttr(item);
  return k && item.plus > 0 ? { [k]: item.plus } : {};
};

/** An item's attributes with its forge pluses added where they went. */
export const itemAttrs = (item: ItemInstance): Partial<Record<GearAttr, number>> => {
  const out = { ...item.attrs };
  const f = forgedOf(item);
  for (const k of Object.keys(f).sort() as GearAttr[]) out[k] = (out[k] ?? 0) + (f[k] ?? 0) * FORGE.perPlusBp;
  return out;
};

/** The level of the bastion's best standing forge (0 without one). */
export const forgeLevel = (s: CampaignState): number => {
  let level = 0;
  for (const key of Object.keys(s.cells).sort()) {
    const b = s.cells[key]?.built;
    if (b?.structure === 'forge' && b.hp > 0 && b.level > level) level = b.level;
  }
  return level;
};

/** The highest plus the bastion's best standing forge allows (0 without one). */
export const forgeMaxPlus = (s: CampaignState): number => {
  const level = forgeLevel(s);
  return level ? (FORGE.maxPlusByLevel[level - 1] ?? FORGE.maxPlus) : 0;
};

/** What stands between a unique piece and its next awakening stage (null: it can be awakened now, given the fuel). */
export type AwakenBlock = 'notUnique' | 'maxAwaken' | 'feat' | 'heroLevel' | 'forgeLevel' | 'noSeal' | 'notEnough' | 'fuel';

/** The next awakening stage of a unique piece (AWAKEN.stages[i]), or null when it has them all. */
export const nextAwaken = (item: ItemInstance): (typeof AWAKEN.stages)[number] | null => (item.unique ? (AWAKEN.stages[item.awaken ?? 0] ?? null) : null);

/** Why a hero can't awaken this piece yet (ignoring the fuel), or null. */
export const awakenBlock = (s: CampaignState, p: PlayerState, item: ItemInstance, byScraps = false): AwakenBlock | null => {
  if (!item.unique) return 'notUnique';
  const st = nextAwaken(item);
  if (!st) return 'maxAwaken';
  // Each stage asks its feat first (docs/UNICAS.md §2.1).
  if (!featDone(p.hero, item)) return 'feat';
  if (p.hero.level < st.level) return 'heroLevel';
  if (forgeLevel(s) < st.forge) return 'forgeLevel';
  if (sealAsked(item) && (p.hero.seals?.[st.seal] ?? 0) < 1) return 'noSeal';
  // An ember of the dragon's heart pays the dust (and the rares).
  // Six scraps pay it too (docs/PANOPLIA.md §4; the command checks them).
  if (!byScraps && s.storage.dust < st.dust && !(p.hero.dragonEmbers ?? 0)) return 'notEnough';
  return null;
};

/** Whether the feat a unique piece's next stage asks is done (docs/UNICAS.md §2.1). */
export const featDone = (h: HeroState, item: ItemInstance): boolean => {
  if (!item.unique) return false;
  // The slot's feat (docs/PANOPLIA.md §3.1): past the one this stage asks (a slot with none left, `step` 5, is past them all).
  const f = h.uniqueSlots?.[item.slot as UniqueSlot]?.feat;
  return !!f && (slotFeat(h, item.unique) === null || f.step > (item.awaken ?? 0) + 1);
};

/**
 * Whether a unique piece's next stage asks its seal (docs/UNICAS.md §2.1): no amulet asks the seals of stages I to III
 * (it comes at level 40, from the Drowned Dragon, and three pieces can't wait in a row for the tide's).
 */
export const sealAsked = (item: ItemInstance): boolean => {
  const st = nextAwaken(item);
  return !!st && !(item.slot === 'amulet' && st.seal !== 'dragon');
};

/** Whether the seal is spent: the dragon's is a permit, not spent (one lets the three pieces reach stage IV). */
export const sealSpent = (item: ItemInstance): boolean => sealAsked(item) && nextAwaken(item)?.seal !== 'dragon';

/** Rare pieces in the bag a hero can melt into an awakening, worst first (the ones the screen offers). */
export const awakenFuel = (p: PlayerState): ItemInstance[] =>
  p.hero.bag.filter((x) => x.rarity === 'rare' && !x.unique).sort((a, b) => itemWorth(a) - itemWorth(b) || a.id - b.id);

/** A seal for a unique's awakening, earned where only that stage's fight gives it. */
export const grantSeal = (s: CampaignState, p: PlayerState, seal: SealId, now: Ms): void => {
  p.hero.seals = { ...p.hero.seals, [seal]: (p.hero.seals?.[seal] ?? 0) + 1 };
  event(s, now, 'seal', { pid: p.id, data: { seal } });
};

/**
 * Awakens a unique piece one stage (its effect grows in gearMods): the attributes that don't stretch grow by
 * AWAKEN.attrStepBp, the others follow the hero's level (docs/UNICAS.md §2.4). A piece from before that keeps the old way.
 */
export const awakenItem = (item: ItemInstance, level = 1): void => {
  item.awaken = (item.awaken ?? 0) + 1;
  if (item.uqBase) {
    refreshUnique(item, level);
    return;
  }
  for (const k of Object.keys(item.attrs).sort() as GearAttr[]) item.attrs[k] = (item.attrs[k] ?? 0) + AWAKEN.attrStepBp;
};

/** What a stretched attribute of a unique piece is worth at a hero level: the middle of a rare one's roll at power level + 4. */
export const uniqueStretch = (slot: GearSlot, level: number): number => {
  const [lo, hi] = GEAR_ROLL_BP.rare;
  return mulBp(mulBp(Math.trunc((lo + hi) / 2), SLOT_BUDGET_BP[slot]), powerBp(level + UNIQUE_STRETCH.powerAdd));
};

/**
 * A unique piece's starting number for an attribute it gets at a reforge (docs/UNICAS.md §2.4): its own slot's, else the
 * unique slot's that brings it (armour and recharge from the armour), else (affinity) the middle of a rare one's range.
 */
export const uniqueAttrBase = (k: GearAttr, slot: GearSlot): number => {
  const own = (UNIQUE_ATTRS as Partial<Record<GearSlot, Partial<Record<GearAttr, number>>>>)[slot]?.[k];
  if (own !== undefined) return own;
  for (const u of GEAR_SLOTS) if (UNIQUE_ATTRS[u][k] !== undefined) return UNIQUE_ATTRS[u][k] as number;
  const [lo, hi] = GEAR_ROLL_BP.rare;
  return Math.trunc((lo + hi) / 2);
};

/** A unique piece's attributes for its hero's level, from its own (docs/UNICAS.md §2.4); pluses are added on top by itemAttrs. */
export const refreshUnique = (item: ItemInstance, level: number): void => {
  if (!item.unique || !item.uqBase) return;
  const stage = (item.awaken ?? 0) * AWAKEN.attrStepBp;
  const attrs: Partial<Record<GearAttr, number>> = {};
  for (const k of Object.keys(item.uqBase).sort() as GearAttr[]) {
    const own = (item.uqBase[k] ?? 0) + stage;
    attrs[k] = STRETCHED_ATTRS.includes(k) ? imax(own, uniqueStretch(item.slot, level)) : own;
  }
  item.attrs = attrs;
};

/** Every unique piece a hero holds, set to its level (on levelling up and when the campaign loads). */
export const refreshUniques = (h: HeroState): void => {
  for (const it of uniquePieces(h)) refreshUnique(it, h.level);
};

const RARITY_RANK: Record<Rarity, number> = { common: 1, uncommon: 2, rare: 3, veryRare: 4, unique: 5 };

/** How good a piece is: rarity first, then its numbers. */
const itemWorth = (it: ItemInstance): number => {
  let sum = 0;
  const a = itemAttrs(it);
  for (const k of Object.keys(a).sort() as GearAttr[]) sum += a[k] ?? 0;
  return RARITY_RANK[it.rarity] * 100000 + sum;
};

/** How good what a hero wears in a slot is (0 = nothing there). */
const slotWorth = (p: PlayerState, slot: GearSlot): number => {
  // A slot the hero hasn't opened yet: it needs the piece least.
  if (p.hero.level < SLOT_LEVEL[slot]) return Number.MAX_SAFE_INTEGER;
  // Rings: the weaker of the two (an empty one is worth nothing).
  const it = slot === 'ring' ? [p.hero.gear.ring, p.hero.gear.ring2].sort((a, b) => (a ? itemWorth(a) : 0) - (b ? itemWorth(b) : 0))[0] : p.hero.gear[slot];
  return it ? itemWorth(it) : 0;
};

/**
 * Who gets a warlord's or boss's piece: anyone who fought it live before the ghosts, then whoever
 * needs that slot most (empty, else the weakest piece), then whoever has won the fewest so far
 * (it rotates), then by id. Not whoever happened to land the last hit.
 */
export const lootRecipient = (s: CampaignState, item: Pick<ItemInstance, 'slot'>, fighters: string[], live: Set<string>): string | null => {
  const pool = s.players.filter((p) => fighters.includes(p.id) && !p.left);
  if (pool.length === 0) return null;
  const anyLive = pool.some((p) => live.has(p.id));
  const cands = anyLive ? pool.filter((p) => live.has(p.id)) : pool;
  cands.sort((a, b) => slotWorth(a, item.slot) - slotWorth(b, item.slot) || (a.hero.warlordLoot ?? 0) - (b.hero.warlordLoot ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return (cands[0] as PlayerState).id;
};

/** Dust to reforge a piece once more: the rarity's price, `stepBp` more for each time it was reforged. */
export const reforgeCost = (item: ItemInstance): number => mulBp(REFORGE.dust[item.rarity], 10000 + REFORGE.stepBp * (item.reforged ?? 0));

/** What `attr` could become at a reforge: an attribute the piece lacks (the one replaced is free again, so an affinity can change type). */
export const reforgeChoices = (item: ItemInstance, attr: GearAttr): GearAttr[] => {
  const rest: Partial<Record<GearAttr, number>> = { ...item.attrs };
  delete rest[attr];
  return openAttrs(rest, item).filter((k) => k !== attr);
};

/**
 * Reforging: `attr` becomes another attribute the piece doesn't have, drawn at random and rolled anew in
 * the rarity's range. The forge pluses on it move to the new attribute. Returns the new attribute.
 */
export const reforgeItem = (s: CampaignState, item: ItemInstance, attr: GearAttr, pick?: GearAttr, keep = !!item.unique, level = 1): GearAttr | null => {
  if (item.attrs[attr] === undefined) return null;
  const pool = reforgeChoices(item, attr);
  if (pool.length === 0 || (pick !== undefined && !pool.includes(pick))) return null;
  const to = pick ?? drawAttr(s.rng, pool);
  // A unique piece picks the attribute, not the value: the rule gives the new one its number (docs/UNICAS.md §2.4).
  if (item.unique && item.uqBase && pick !== undefined) {
    const f = forgedOf(item);
    const base: Partial<Record<GearAttr, number>> = {};
    for (const k of Object.keys(item.uqBase).sort() as GearAttr[]) base[k === attr ? to : k] = k === attr ? uniqueAttrBase(to, item.slot) : (item.uqBase[k] as number);
    item.uqBase = base;
    refreshUnique(item, level);
    if (item.plus > 0) {
      const moved: Partial<Record<GearAttr, number>> = {};
      for (const k of Object.keys(f).sort() as GearAttr[]) moved[k === attr ? to : k] = (moved[k === attr ? to : k] ?? 0) + (f[k] ?? 0);
      item.forged = moved;
    }
    item.reforged = (item.reforged ?? 0) + 1;
    return to;
  }
  // A unique piece's picked attribute keeps the value; a drawn one, or a directed reforge's pick, rolls anew in the rarity's range.
  const [lo, hi] = pick !== undefined && keep ? [item.attrs[attr] as number, item.attrs[attr] as number] : rollRange(item, item.rarity, to);
  // Where the pluses went, read before the attributes change (older pieces put them on the main one).
  const f = forgedOf(item);
  const attrs: Partial<Record<GearAttr, number>> = {};
  for (const k of Object.keys(item.attrs).sort() as GearAttr[]) attrs[k === attr ? to : k] = k === attr ? rngRange(s.rng, lo, hi) : (item.attrs[k] as number);
  item.attrs = attrs;
  if (item.plus > 0) {
    const moved: Partial<Record<GearAttr, number>> = {};
    for (const k of Object.keys(f).sort() as GearAttr[]) moved[k === attr ? to : k] = (moved[k === attr ? to : k] ?? 0) + (f[k] ?? 0);
    item.forged = moved;
  }
  item.reforged = (item.reforged ?? 0) + 1;
  return to;
};

/** Forge cost of the next plus: 25 × level, in dream dust. */
export const forgeCost = (item: ItemInstance): number => FORGE.dustPerLevel * (item.plus + 1);

/** Whether a hero already holds this unique piece (worn or in the bag). */
export const hasUnique = (p: PlayerState, slot: UniqueSlot): boolean => {
  const id = uniqueFor(p.hero.heroClass, slot).id;
  return uniquePieces(p.hero).some((x) => x.unique === id);
};

/** The unique piece of the hero's class for a slot, bound to it (once). */
export const giveUnique = (s: CampaignState, p: PlayerState, slot: UniqueSlot, now: Ms): ItemInstance | null => {
  if (hasUnique(p, slot)) return null;
  const def = uniqueFor(p.hero.heroClass, slot);
  const item: ItemInstance = { id: s.nextId++, slot, rarity: 'unique', attrs: { ...def.attrs }, effect: def.effect, plus: 0, unique: def.id, uqBase: { ...def.attrs } };
  refreshUnique(item, p.hero.level);
  // Its slot's first feat starts now (docs/UNICAS.md §2.1): what was done before the piece came doesn't count. A piece
  // coming to a slot that already has one takes what was worked there (docs/PANOPLIA.md §3).
  const other = uniquePieces(p.hero).find((x) => x.slot === slot && x.unique && UNIQUES[x.unique].cls === def.cls);
  ensureSlot(p.hero, slot);
  giveItem(s, p, item, now, def.id);
  if (other) syncSlot(p.hero, other);
  return item;
};

/**
 * A chapter's unique piece for a hero still under the level it asks: kept until the hero gets there
 * (docs/PROGRESION.md). Given at once if the hero is already there.
 */
export const promiseUnique = (s: CampaignState, p: PlayerState, slot: UniqueSlot, level: number, now: Ms): void => {
  if (hasUnique(p, slot)) return;
  if (p.hero.level >= level) {
    giveUnique(s, p, slot, now);
    return;
  }
  const kept = (p.hero.pendingUniques ??= []);
  if (!kept.some((x) => x.slot === slot)) kept.push({ slot, level });
  event(s, now, 'uniqueKept', { pid: p.id, data: { slot, level } });
};

/** The unique pieces a hero was keeping for a level it has now reached. */
export const claimUniques = (s: CampaignState, p: PlayerState, now: Ms): void => {
  const kept = p.hero.pendingUniques;
  if (!kept?.length) return;
  const due = kept.filter((x) => p.hero.level >= x.level);
  if (due.length === 0) return;
  p.hero.pendingUniques = kept.filter((x) => p.hero.level < x.level);
  if (p.hero.pendingUniques.length === 0) delete p.hero.pendingUniques;
  for (const x of due) giveUnique(s, p, x.slot, now);
};
